import { create } from 'zustand';

/**
 * Product mode — determines default feature set.
 *
 * - 'full': Expose-API full (all features enabled by default)
 * - 'mini': Mini-Expose (core features only, others opt-in via flags)
 */
export type ProductMode = 'full' | 'mini';

/**
 * Feature flags for controlling module visibility.
 *
 * Each flag controls a UI module. When disabled:
 * - Routes are not rendered (404 redirect)
 * - Sidebar menu items are hidden
 * - Lazy imports are never loaded (zero bundle cost)
 *
 * For Mini-Expose clients who need X-Road or Governance,
 * enable the flag in their configuration.
 */
export interface FeatureFlags {
  /** Dashboard page */
  dashboard: boolean;
  /** Data sources management */
  datasources: boolean;
  /** Schema explorer */
  schemaExplorer: boolean;
  /** API endpoints */
  endpoints: boolean;
  /** Sync & push */
  sync: boolean;
  /** X-Road integration (publishing, subscriptions, catalog) */
  xroad: boolean;
  /** Data governance (domains, glossary, lineage, quality, catalog) */
  governance: boolean;
  /** DataLens AI analysis */
  datalens: boolean;
  /** Sync test page (dev tool) */
  syncTest: boolean;
  /** Multi-tenant portals (Operator, ControlTower, Managed, Light) */
  portals: boolean;
  /** Query builder (accessible via URL even if hidden from nav) */
  queryBuilder: boolean;
  /** JSONata playground and UDF manager */
  jsonata: boolean;
  /** Data Quality engine (DQ dashboard, rules, profiling, gate, alerts) */
  dq: boolean;
  /** Privacy & Protection (registry, DCP cartography, CDP compliance) */
  privacy: boolean;
}

/** Default flags for Expose-API full — everything enabled */
const FULL_DEFAULTS: FeatureFlags = {
  dashboard: true,
  datasources: true,
  schemaExplorer: true,
  endpoints: true,
  sync: true,
  xroad: true,
  governance: true,
  datalens: true,
  syncTest: true,
  portals: true,
  queryBuilder: true,
  jsonata: true,
  dq: true,
  privacy: true,
};

/** Default flags for Mini-Expose — core only, rest opt-in */
const MINI_DEFAULTS: FeatureFlags = {
  dashboard: true,
  datasources: true,
  schemaExplorer: true,
  endpoints: true,
  sync: true,
  xroad: false,       // activable par client
  governance: false,   // activable par client
  datalens: false,     // activable par client
  syncTest: false,
  portals: false,
  queryBuilder: true,
  jsonata: false,
  dq: false,
  privacy: false,
};

interface FeaturesState {
  productMode: ProductMode;
  flags: FeatureFlags;
  isEnabled: (feature: keyof FeatureFlags) => boolean;
  enableFeature: (feature: keyof FeatureFlags) => void;
  disableFeature: (feature: keyof FeatureFlags) => void;
  setProductMode: (mode: ProductMode) => void;
}

/**
 * Detect product mode from Vite environment variable.
 * Set VITE_PRODUCT=mini-expose in .env or at build time.
 */
function detectProductMode(): ProductMode {
  const envProduct = import.meta.env.VITE_PRODUCT;
  if (envProduct === 'mini-expose' || envProduct === 'mini') {
    return 'mini';
  }
  return 'full';
}

/**
 * Load feature flag overrides from environment.
 * Format: VITE_FEATURE_XROAD=true, VITE_FEATURE_GOVERNANCE=true, etc.
 */
function loadEnvOverrides(defaults: FeatureFlags): FeatureFlags {
  const flags = { ...defaults };
  const featureKeys = Object.keys(flags) as (keyof FeatureFlags)[];

  for (const key of featureKeys) {
    const envKey = `VITE_FEATURE_${key.toUpperCase()}`;
    const envValue = import.meta.env[envKey];
    if (envValue === 'true') flags[key] = true;
    if (envValue === 'false') flags[key] = false;
  }

  return flags;
}

const useFeaturesStore = create<FeaturesState>((set, get) => {
  const mode = detectProductMode();
  const defaults = mode === 'mini' ? MINI_DEFAULTS : FULL_DEFAULTS;
  const flags = loadEnvOverrides(defaults);

  return {
    productMode: mode,
    flags,

    isEnabled: (feature) => get().flags[feature],

    enableFeature: (feature) =>
      set((state) => ({
        flags: { ...state.flags, [feature]: true },
      })),

    disableFeature: (feature) =>
      set((state) => ({
        flags: { ...state.flags, [feature]: false },
      })),

    setProductMode: (mode) =>
      set({
        productMode: mode,
        flags: loadEnvOverrides(mode === 'mini' ? MINI_DEFAULTS : FULL_DEFAULTS),
      }),
  };
});

export default useFeaturesStore;
